import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { computed, type ComputedRef } from 'vue';
import type {
  ComponentSubTypeDto,
  ComponentTypeDto,
  SetComponentTypeFieldsBulkCell,
  UpdateSubTypeArgs,
} from '@/ApiClient';
import { signalrListen } from '@lib/signalr';

export const useComponentTypes = defineStore('component-types', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.componentType_GetComponentTypes,
    dto: 'ComponentTypeDto',
    create: api.componentType_AddType,
    update: api.componentType_UpdateType,
    delete: api.componentType_DeleteType,
    signalR: 'ComponentTypeUpdate',
  });
  const { loaded } = globalStoreBase;

  const allSubTypes: ComputedRef<ComponentSubTypeDto[]> = computed(() => {
    return loaded.value.map((t) => t.subTypes || []).flat();
  });

  function getSubTypeFromId(id: number | null | undefined) {
    if (id === null) return null;

    return loaded.value
      .map((t) => t.subTypes)
      .flat()
      .find((ct) => ct?.id === id);
  }

  async function addType(name: string, componentNumberCode?: string) {
    const existingTypeWithSameName = loaded.value.find((type) => type.name?.toLowerCase() === name.toLowerCase());

    if (existingTypeWithSameName) {
      return existingTypeWithSameName;
    }

    const componentType = await globalStoreBase.create({ name, componentNumberCode });
    loaded.value.sort((a, b) => a.name?.localeCompare(b?.name || '') || 0);

    return componentType;
  }

  /**
   * Every sub-type endpoint answers with the whole type it belongs to, and the answers can land out
   * of order: two edits to one type in flight, and the earlier response arriving last would put the
   * first edit's view of the type — with the second edit undone — back into the store until the next
   * reload. So each write to a type takes a number, and only the latest write's answer is kept.
   */
  const latestWrite = new Map<number, number>();

  function beginWrite(typeId: number) {
    lastWriteStartedAt = performance.now();
    const sequence = (latestWrite.get(typeId) ?? 0) + 1;
    latestWrite.set(typeId, sequence);
    return sequence;
  }

  function replaceType(updatedComponentType: ComponentTypeDto, sequence: number) {
    if (latestWrite.get(updatedComponentType.id) !== sequence) return;

    loaded.value = loaded.value.map((t) => (t.id !== updatedComponentType.id ? t : updatedComponentType));
  }

  const typeIdOfSubType = (subTypeId: number) =>
    loaded.value.find((type) => type.subTypes.some((subType) => subType.id === subTypeId))?.id;

  /** `name` null makes the nameless sub-type, the one a component of the type picks when it has no sub-type. */
  async function addSubType(typeId: number, name: string | null, componentNumberCode?: string) {
    const brandId = useUser().brandId;

    const componentType = loaded.value.find((type) => type.id === typeId);

    const existingSubTypeWithSameName = componentType?.subTypes?.find((st) => st.name === name);

    if (existingSubTypeWithSameName) {
      return componentType;
    }

    const sequence = beginWrite(typeId);
    const updatedComponentType = await api.componentType_AddSubType(brandId, typeId, {
      name: name ?? undefined,
      componentNumberCode,
    });
    replaceType(updatedComponentType, sequence);

    return updatedComponentType;
  }

  async function updateSubType(subTypeId: number, args: UpdateSubTypeArgs) {
    const sequence = beginWrite(typeIdOfSubType(subTypeId) ?? 0);
    replaceType(await api.componentType_UpdateSubType(useUser().brandId, subTypeId, args), sequence);
  }

  async function deleteSubType(subTypeId: number) {
    const sequence = beginWrite(typeIdOfSubType(subTypeId) ?? 0);
    replaceType(await api.componentType_DeleteSubType(useUser().brandId, subTypeId), sequence);
  }

  /**
   * Many cells in one request, answered with the whole list — the same list a reload fetches, already
   * holding the write. Applied at once, so the table shows the saved values without waiting for the
   * SignalR round trip.
   */
  async function setFieldsBulk(cells: SetComponentTypeFieldsBulkCell[]) {
    lastWriteStartedAt = performance.now();
    globalStoreBase.replaceAll(await api.componentType_SetFieldsBulk(useUser().brandId, { cells }));
  }

  /**
   * A reload that set out before the latest write cannot hold it, and landing after the write's own
   * answer it would put the old values back for as long as the next reload takes. Such a reload is
   * dropped; the write's own SignalR event brings a fresh one.
   */
  let lastWriteStartedAt = 0;

  async function loadAll() {
    const startedAt = performance.now();
    const fresh = await api.componentType_GetComponentTypes(useUser().brandId);
    if (startedAt < lastWriteStartedAt) return loaded.value;

    globalStoreBase.replaceAll(fresh);
    return loaded.value;
  }

  function listen() {
    signalrListen('ComponentTypeUpdate', loadAll, { listenForever: true });
  }

  /** What uses each sub-type, for the dialog that asks before deleting. */
  function usages(subTypeIds: number[]) {
    return api.componentType_GetUsages(useUser().brandId, subTypeIds);
  }

  /** Whole types and single sub-types in one request, taken out of the list here as the base store's delete does. */
  async function deleteMany(typeIds: number[], subTypeIds: number[]) {
    lastWriteStartedAt = performance.now();
    await api.componentType_DeleteMany(useUser().brandId, { typeIds, subTypeIds });

    const goneTypes = new Set(typeIds);
    const goneSubTypes = new Set(subTypeIds);
    loaded.value = loaded.value
      .filter((type) => !goneTypes.has(type.id))
      .map((type) => ({ ...type, subTypes: type.subTypes.filter((subType) => !goneSubTypes.has(subType.id)) }));
  }

  /**
   * Whole types as types, single sub-types within their own type, one request. Answers with the
   * types touched — new and changed — after putting them into the list.
   */
  async function copyMany(typeIds: number[], subTypeIds: number[]) {
    lastWriteStartedAt = performance.now();
    const touched = await api.componentType_Copy(useUser().brandId, { typeIds, subTypeIds });
    const touchedIds = new Set(touched.map((type) => type.id));

    loaded.value = [...loaded.value.filter((type) => !touchedIds.has(type.id)), ...touched].sort(
      (a, b) => a.name?.localeCompare(b?.name || '') || 0
    );

    return touched;
  }

  return {
    ...globalStoreBase,
    create: undefined, // Use addType instead
    loadAll,
    listen,
    loaded,
    allSubTypes,
    getSubTypeFromId,
    addType,
    addSubType,
    updateSubType,
    deleteSubType,
    setFieldsBulk,
    usages,
    deleteMany,
    copyMany,
  };
});
